
















































































import { Component, Prop, Vue } from "vue-property-decorator";
import MiraAnimatedTextComponent from "@/components/mira/MiraAnimatedText.vue";
import {
  COLOR_COCKPIT_MANAGEMENT,
  COLOR_COCKPIT_MANAGEMENT_DARK,
  COLOR_DARK_FILL_TEXT,
  COLOR_MIRA_BAR,
  COLOR_MIRA_BAR_DARK,
  COLOR_MIRA_STATUS_CRITICAL_CHIP,
  COLOR_MIRA_STATUS_GOOD_CHIP,
  COLOR_MIRA_STATUS_WARNING_CHIP,
  COLOR_MIRA_TILE_SELECTED,
  COLOR_MIRA_TILE_SELECTED_DARK,
} from "@/utils/colors";
import { MiraCoverageStatus } from "@/utils/MiraDataQuality";
import { MiraSegment, MiraViewDelta, miraDeltaColor } from "@/utils/MiraDataQualityView";
import { MiraValueUnit, splitValueUnit } from "@/utils/MiraValueUnit";
import { themed, themedFill, themedFillTextColor } from "@/utils/Theme";

const STATUS_CHIP: Record<MiraCoverageStatus, string> = {
  [MiraCoverageStatus.GOOD]: COLOR_MIRA_STATUS_GOOD_CHIP,
  [MiraCoverageStatus.WARNING]: COLOR_MIRA_STATUS_WARNING_CHIP,
  [MiraCoverageStatus.CRITICAL]: COLOR_MIRA_STATUS_CRITICAL_CHIP,
};

const STATUS_ICON: Record<MiraCoverageStatus, string> = {
  [MiraCoverageStatus.GOOD]: "mdi-check-circle",
  [MiraCoverageStatus.WARNING]: "mdi-alert",
  [MiraCoverageStatus.CRITICAL]: "mdi-alert-octagon",
};

/**
 * One Mira headline figure, in the shape KPI cards share across the market: label, a value that
 * dwarfs everything else, the change against the comparison period, one context line, and a status.
 * A square like the Cockpit's tiles from 600px (Hans, #2610102); a tile whose texts need more room
 * grows taller rather than clipping them, and MiraKpiTileGrid stretches the others to match.
 *
 * The tile itself stays neutral; colour sits only on the status chip and the delta arrow, and the
 * status always carries an icon and a word, never colour alone. The icon avatar takes the cockpit's
 * management tint so the screen reads as part of the same app. Presentational only: every string
 * arrives translated and every number formatted.
 */
@Component({
  components: {
    "mira-animated-text": MiraAnimatedTextComponent,
  },
})
export default class MiraKpiTileComponent extends Vue {
  @Prop({ required: true })
  icon!: string;

  @Prop({ required: true })
  title!: string;

  @Prop({ required: true })
  value!: string;

  @Prop({ default: null })
  caption!: string | null;

  @Prop({ default: null })
  delta!: MiraViewDelta | null;

  /** A coverage status; a figure without one (a count) passes `null`. */
  @Prop({ default: null })
  status!: MiraCoverageStatus | null;

  @Prop({ default: "" })
  statusLabel!: string;

  /** The P15 to P85 spread, in the tile's unit. Shown under the caption when {@link showPercentiles}. */
  @Prop({ default: null })
  spread!: string | null;

  /** A second figure under the caption, e.g. Geräte je Projekt's HyDry Control line (#2610137). */
  @Prop({ default: null })
  extraLine!: string | null;

  /** A part-to-whole split drawn as a thin bar with a one-line legend (#2610137). */
  @Prop({ default: null })
  parts!: MiraSegment[] | null;

  /** Whether the screen allows the percentile spread (`VIEW_MIRA_PERCENTILES`). */
  @Prop({ default: false })
  showPercentiles!: boolean;

  /** Whether clicking the tile selects its figure for the Verlauf card. */
  @Prop({ default: false })
  selectable!: boolean;

  /** Whether this tile's figure is the one the Verlauf card is showing. */
  @Prop({ default: false })
  selected!: boolean;

  /** The parts with a share to draw; an empty one keeps its place in the legend only. */
  get visibleParts(): MiraSegment[] {
    return (this.parts ?? []).filter((part) => part.value > 0);
  }

  /** The bar as one sentence for screen readers. */
  get partsLabel(): string {
    return (this.parts ?? []).map((part) => `${part.label}: ${part.shareText}`).join(", ");
  }

  get valueParts(): MiraValueUnit {
    return splitValueUnit(this.value);
  }

  get selectedStyle(): Record<string, string> {
    const color = themed(COLOR_MIRA_TILE_SELECTED, COLOR_MIRA_TILE_SELECTED_DARK);
    return this.selected ? { boxShadow: `0 0 0 2px ${color}` } : {};
  }

  onSelect() {
    if (this.selectable) {
      this.$emit("select");
    }
  }

  get iconBackground() {
    return themed(COLOR_COCKPIT_MANAGEMENT, COLOR_COCKPIT_MANAGEMENT_DARK);
  }

  get iconColor() {
    return themed(COLOR_MIRA_BAR, COLOR_MIRA_BAR_DARK);
  }

  get chipColor() {
    return this.status ? themedFill(STATUS_CHIP[this.status], "status") : "";
  }

  get chipTextColor() {
    return this.status ? themedFillTextColor(STATUS_CHIP[this.status], COLOR_DARK_FILL_TEXT, "status") : "";
  }

  get statusIcon() {
    return this.status ? STATUS_ICON[this.status] : "";
  }

  /** Up or down only: an unchanged value is written "±0", and a dash beside it would say it twice. */
  get deltaIcon() {
    return this.delta?.direction === "up" ? "mdi-arrow-up" : "mdi-arrow-down";
  }

  /** For coverage, up is always good. */
  get deltaColor() {
    return miraDeltaColor(this.delta?.direction ?? "flat", this.$vuetify.theme.dark, this.delta?.tone);
  }
}
